/* =========================================================================
   Data model, constants and pure helpers.

   recipe = {
     id, pos, title, category, tags[],
     serves,                       // ORIGINAL — never modified by scaling
     ingredients: [{qty, unit, item}],
     method: [step, ...],
     source: {name, url},
     heroImage: Blob|null, heroAspect,
     images: Blob[], imageAspects: [],
     notes:    [{text, date}],
     pairings: [{text, date}],
     emoji, createdAt
   }

   Ported from iteration 1 unchanged except where noted.
   ========================================================================= */

export const CATEGORIES = [
  'Fish', 'Meat', 'Chicken', 'Vegetarian', 'Salad',
  'Pasta & Grains', 'Soup', 'Dessert', 'Baking', 'Other',
]

export const CAT_EMOJI = {
  'Fish': '🐟', 'Meat': '🥩', 'Chicken': '🍗', 'Vegetarian': '🥦', 'Salad': '🥗',
  'Pasta & Grains': '🍝', 'Soup': '🍲', 'Dessert': '🍰', 'Baking': '🥖', 'Other': '🍽️',
}

export function esc(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;')
    .replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}

export function uid() {
  return 'r' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6)
}

const FRACTIONS = [[0.25, '¼'], [0.33, '⅓'], [0.5, '½'], [0.66, '⅔'], [0.75, '¾']]

export function fmtQty(n) {
  if (n == null || isNaN(n)) return ''
  const whole = Math.floor(n), frac = n - whole
  let fs = ''
  if (frac > 0.05) {
    let best = null, bd = 1
    for (const [v, sym] of FRACTIONS) { const d = Math.abs(frac - v); if (d < bd) { bd = d; best = sym } }
    if (bd <= 0.09) fs = best
  }
  if (fs) return (whole > 0 ? whole + ' ' : '') + fs
  return (Math.round(n * 100) / 100).toString()
}

export function scaledIngredients(r, serves) {
  const f = serves / r.serves
  return r.ingredients.map(ing => ({ ...ing, sq: ing.qty == null ? null : ing.qty * f }))
}

export function ingLine(ing) {
  const q = ing.sq !== undefined ? ing.sq : ing.qty
  const qs = q == null ? '' : fmtQty(q)
  return (qs ? qs + (ing.unit ? ' ' + ing.unit : '') + ' ' : (ing.unit ? ing.unit + ' ' : '')) + ing.item
}

export function heroAspect(r) { return r.heroAspect || 16 / 9 }
export function imgAspect(r, i) { return (r.imageAspects && r.imageAspects[i]) || 4 / 3 }

/* ---------------- ingredient line parsing ---------------- */

const UNITS = [
  'kg', 'g', 'mg', 'l', 'ml', 'cup', 'cups', 'tbsp', 'tsp', 'teaspoon', 'teaspoons',
  'tablespoon', 'tablespoons', 'can', 'cans', 'tin', 'tins', 'clove', 'cloves',
  'sprig', 'sprigs', 'bunch', 'slice', 'slices', 'head', 'piece', 'pieces',
  'stick', 'sticks', 'pinch', 'handful', 'rasher', 'rashers',
]

export function parseIngLine(line) {
  line = line.replace(/^[•\-*–]\s*/, '').trim()
  if (!line) return null
  const UF = { '¼': .25, '½': .5, '¾': .75, '⅓': 1 / 3, '⅔': 2 / 3, '⅛': .125 }
  let qty = null, rest = line
  const m = line.match(/^(\d+\s*[¼½¾⅓⅔⅛]|\d+\s*\/\s*\d+|\d+(?:[.,]\d+)?|[¼½¾⅓⅔⅛])\s*(.*)$/)
  if (m) {
    let qs = m[1].replace(',', '.').trim(); rest = m[2]
    if (/\//.test(qs)) { const [a, b] = qs.split('/').map(Number); qty = a / b }
    else if (UF[qs] !== undefined) { qty = UF[qs] }
    else { const um = qs.match(/^(\d+)\s*([¼½¾⅓⅔⅛])$/); qty = um ? Number(um[1]) + UF[um[2]] : parseFloat(qs) }
  }
  let unit = ''
  const wm = rest.match(/^([A-Za-z]+)\s+(.*)$/)
  if (wm && UNITS.includes(wm[1].toLowerCase())) { unit = wm[1].toLowerCase(); rest = wm[2] }
  return { qty, unit, item: rest.trim() || line }
}

/* =========================================================================
   Text tidying — house style
   -------------------------------------------------------------------------
   Ingredients: classic cookbook style, lowercase first word.
   Method:      sentence case, ending in a full stop.

   Capitalising is always safe. Lowercasing is not — "Parmesan" must not
   become "parmesan", and "Lebanese bread" must not become "lebanese bread".
   So the first word is only lowercased when it isn't a proper noun.

   This list will never be exhaustive. If a word gets wrongly lowercased,
   add it here — that's the intended way to fix it.
   ========================================================================= */

const KEEP_CAPITALISED = new Set([
  // cheeses
  'parmesan', 'parmigiano', 'reggiano', 'pecorino', 'gruyere', 'gruyère', 'cheddar',
  'roquefort', 'gorgonzola', 'manchego', 'halloumi', 'comté', 'comte', 'brie',
  'camembert', 'stilton', 'emmental', 'mozzarella', 'provolone', 'taleggio',
  'gouda', 'edam', 'jarlsberg', 'asiago', 'burrata', 'mascarpone',
  // places and nationalities
  'lebanese', 'greek', 'italian', 'french', 'spanish', 'turkish', 'moroccan',
  'thai', 'japanese', 'chinese', 'korean', 'vietnamese', 'indian', 'mexican',
  'english', 'irish', 'scottish', 'welsh', 'persian', 'israeli', 'cypriot',
  'sicilian', 'tuscan', 'roman', 'neapolitan', 'swiss', 'german', 'danish',
  'swedish', 'norwegian', 'russian', 'polish', 'hungarian', 'egyptian',
  'ethiopian', 'cajun', 'creole', 'kashmiri', 'sichuan', 'szechuan',
  'cantonese', 'balinese', 'malaysian', 'indonesian', 'filipino', 'peruvian',
  'brazilian', 'argentinian', 'portuguese', 'basque', 'catalan', 'provencal',
  'provençal', 'calabrian', 'ligurian', 'umbrian', 'venetian', 'florentine',
  'continental', 'american', 'australian',
  // named products, varietals and brands
  'dijon', 'worcestershire', 'tabasco', 'maldon', 'aleppo', 'espelette',
  'marsala', 'madeira', 'prosecco', 'champagne', 'chardonnay', 'pinot',
  'merlot', 'shiraz', 'cabernet', 'sauvignon', 'riesling', 'cognac',
  'armagnac', 'calvados', 'kirsch', 'cointreau', 'campari', 'aperol',
  'pernod', 'ouzo', 'sambuca', 'amaretto', 'frangelico', 'chartreuse',
  'angostura', 'bourbon', 'scotch', 'guinness', 'nutella', 'vegemite',
  'kewpie', 'grand', 'san', 'granny', 'meyer', 'valencia', 'seville',
  'medjool', 'kalamata', 'puy', 'arborio', 'carnaroli', 'basmati', 'jasmine',
  'bomba', 'castelvetrano', 'nicoise', 'niçoise', 'cavolo', 'lacinato',
  'padron', 'padrón', 'roma', 'kipfler', 'desiree', 'dutch', 'kipflers',
])

/** Does this word need its capital left alone? */
function keepsCapital(word) {
  const bare = word.replace(/[^\p{L}'’-]/gu, '')
  if (!bare) return true
  if (bare === bare.toUpperCase() && bare.length > 1) return true   // EVOO, LSA
  if (/\p{Lu}/u.test(bare.slice(1))) return true                    // McIntosh, iSi
  return KEEP_CAPITALISED.has(bare.toLowerCase())
}

/** Ingredient text → classic cookbook style (lowercase first word). */
export function tidyIngredientItem(item) {
  const s = String(item == null ? '' : item).trim()
  if (!s) return s
  const first = s.split(/\s+/)[0]
  if (keepsCapital(first)) return s
  return s.charAt(0).toLowerCase() + s.slice(1)
}

/** Method text → sentence case, ending in a full stop. */
export function tidyMethodStep(step) {
  let s = String(step == null ? '' : step).trim()
  if (!s) return s
  s = s.charAt(0).toUpperCase() + s.slice(1)
  if (!/[.!?:）)”"']$/.test(s)) s += '.'
  return s
}

/** Apply house style to a whole recipe's ingredients and method, in place. */
export function tidyRecipeText(r) {
  if (Array.isArray(r.ingredients)) {
    r.ingredients = r.ingredients.map(i => ({ ...i, item: tidyIngredientItem(i.item) }))
  }
  if (Array.isArray(r.method)) {
    r.method = r.method.map(tidyMethodStep).filter(Boolean)
  }
  return r
}

/**
 * Normalise any recipe object (from seed, import, or migration) into the
 * shape the app expects. Defensive because imported files come from
 * iteration 1, from other people's exports, and eventually from AI parsing.
 */
export function normaliseRecipe(r, index = 0) {
  const out = {
    id: r.id || uid(),
    pos: typeof r.pos === 'number' ? r.pos : index,
    title: String(r.title || 'Untitled recipe'),
    category: CATEGORIES.includes(r.category) ? r.category : 'Other',
    tags: Array.isArray(r.tags) ? r.tags.filter(Boolean).map(String) : [],
    serves: Math.max(1, parseInt(r.serves, 10) || 4),
    ingredients: Array.isArray(r.ingredients)
      ? r.ingredients.map(i =>
          typeof i === 'string'
            ? parseIngLine(i)
            : { qty: i.qty ?? null, unit: i.unit || '', item: String(i.item || '') })
        .filter(Boolean)
      : [],
    method: Array.isArray(r.method) ? r.method.map(String).filter(Boolean) : [],
    source: {
      name: (r.source && r.source.name) || '',
      url: (r.source && r.source.url) || '',
    },
    heroImage: r.heroImage ?? null,
    heroAspect: r.heroAspect || 16 / 9,
    images: Array.isArray(r.images) ? r.images.filter(Boolean) : [],
    imageAspects: Array.isArray(r.imageAspects) ? r.imageAspects : [],
    notes: Array.isArray(r.notes) ? r.notes.filter(Boolean) : [],
    pairings: Array.isArray(r.pairings) ? r.pairings.filter(Boolean) : [],
    emoji: r.emoji || CAT_EMOJI[r.category] || '🍽️',
    createdAt: r.createdAt || new Date().toISOString(),
  }
  return tidyRecipeText(out)
}
